import { forwardRef } from 'react';

import { cva } from 'cva';
import {
  LabelContext,
  Label as RACLabel,
  type LabelProps as RACLabelProps,
} from 'react-aria-components/Label';
import { useContextProps } from 'react-aria-components/slots';

import styles from './label.module.css';

export const labelVariants = cva({
  base: styles.label,
  variants: {
    isRequired: {
      true: styles.with_required_dot,
    },
  },
});

export type LabelProps = RACLabelProps & {
  isRequired?: boolean;
};

/**
 * Label component that can be used for form fields.
 *
 * Use `isRequired` to indicate required fields.
 *
 * ```tsx
 * <Label isRequired>
 *  Hello, world!
 * </Label>
 * ```
 */

export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({ children, className, isRequired, ...props }, ref) => {
    // Merge the local props and ref with the ones provided via context.
    // see also: https://react-spectrum.adobe.com/react-aria/TextField.html
    const [mergedProps, mergedRef] = useContextProps(props, ref, LabelContext);
    const classes = labelVariants({
      isRequired,
      className,
    });
    if (!children) {
      return;
    }

    return (
      <div className={styles.label_wrapper}>
        <RACLabel ref={mergedRef} {...mergedProps} className={classes}>
          {children}
        </RACLabel>
        {isRequired && <span className="dot" />}
      </div>
    );
  },
);
